import { PanelTable } from "../PanelTable/PanelTable";
import type { PanelTableColumn } from "../PanelTable/types";
import type { TableAction } from "@shared/types/data-table";
import { TableRow, TableCell } from "@shared/components/ui/table";
import { cn } from "@shared/lib/utils";

export interface SummaryRow {
    label: string;
    value: string;
    isFinal?: boolean;
}

export interface ItemsTableWithSummaryProps<T extends object> {
    data: T[];
    columns: PanelTableColumn<T>[];
    summaryRows: SummaryRow[];
    variant?: 'panel' | 'full' | 'expandable';
    rowActions?: (item: T) => TableAction<T>[];
    emptyMessage?: string;
    loading?: boolean;
    showPagination?: boolean;
    pageSize?: number;
    className?: string;
    labelColSpan?: number;
}

export function ItemsTableWithSummary<T extends object>({
    data,
    columns,
    summaryRows,
    variant = 'full',
    rowActions,
    emptyMessage = "No items available",
    loading = false,
    showPagination = false,
    pageSize = 5,
    className,
    labelColSpan = 2
}: ItemsTableWithSummaryProps<T>) {
    // Calculate the action column span (1 if rowActions exist, 0 otherwise)
    const actionColSpan = rowActions ? 1 : 0;

    const footer = (
        <>
            {summaryRows.map((row, index) => (
                <TableRow key={`summary-${index}`}>
                    <TableCell colSpan={labelColSpan} />
                    <TableCell
                        className={cn(
                            "text-right text-sm font-medium p-2",
                            row.isFinal && "border-t"
                        )}
                    >
                        {row.label}
                    </TableCell>
                    <TableCell
                        className={cn(
                            "text-right text-sm p-2",
                            row.isFinal && "border-t font-semibold"
                        )}
                    >
                        {row.value}
                    </TableCell>
                    {actionColSpan > 0 && <TableCell />}
                </TableRow>
            ))}
        </>
    );

    return (
        <PanelTable<T>
            data={data}
            columns={columns}
            variant={variant}
            showPagination={showPagination}
            pageSize={pageSize}
            rowActions={rowActions}
            emptyMessage={emptyMessage}
            loading={loading}
            className={className}
            footer={footer}
        />
    );
}
